import { Component, OnInit } from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { UserService } from '../../services/user.service';
import { LeftSidebarMenuService } from './services/left-sidebar-menu.service';
import { LabelValue } from '../../models/label-value.interface';
import { FormGroup, FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidebarService } from '../../services/dashboard/sidebar.service';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  skip,
  filter,
  tap,
} from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, of } from 'rxjs';
import { PermissionService } from '../../guards/permission/service/permission.service';

interface MenuAnchor {
  label: string;
  route: string;
  icon: string;
}

@UntilDestroy()
@Component({
  selector: 'app-left-sidebar-menu',
  templateUrl: './left-sidebar-menu.component.html',
  styleUrls: ['./left-sidebar-menu.component.scss'],
  animations: [
    trigger('panelAnimation', [
      state(
        'closed',
        style({
          height: '0',
          opacity: 0,
          overflow: 'hidden',
          padding: '0 1rem 0',
        })
      ),
      state(
        'open',
        style({
          height: '*',
          opacity: 1,
          padding: '0 1rem 0.5rem',
        })
      ),
      transition('closed => open', [animate('200ms ease-out')]),
      transition('open => closed', [animate('200ms ease-in')]),
    ]),
  ],
})
export class LeftSidebarMenuComponent implements OnInit {
  isExpanded$ = this.leftSidebarMenuService.getIsSidebarExpanded$();

  isFundingOpen = true;
  isCollateralOpen = true;
  isSettlementOpen = true;

  isTenantUser = this.userService.isTenant;
  isAdminPage = false;
  allSubTenants: LabelValue[] = [];
  collateralWorkflowStatusCounts$ = this.permissionService.hasPermission({
    permission: 'COLLATERAL_DASHBOARD',
  })
    ? this.leftSidebarMenuService.collateralStatusCount$
    : of([]);
  settlementWorkflowStatusCounts$ =
    this.leftSidebarMenuService.settlementStatusCount$;
  fundingWorkflowStatusCounts$ =
    this.leftSidebarMenuService.fundingStatusCount$;

  subTenantForm = new FormGroup({
    subTenantsDropdown: new FormControl<LabelValue[]>([]),
  });

  mainSectionAnchors: MenuAnchor[] = [
    {
      label: 'Funding',
      route: '/secure/funding/funding-pipeline',
      icon: 'pi pi-money-bill',
    },
    {
      label: 'Collateral',
      route: '/secure/collateral/collateral-pipeline',
      icon: 'pi pi-send',
    },
    {
      label: 'Settlement',
      route: '/secure/settlement/settlement-landing',
      icon: 'pi pi-dollar',
    },
  ];

  constructor(
    private userService: UserService,
    private leftSidebarMenuService: LeftSidebarMenuService,
    private sidebarService: SidebarService,
    private router: Router,
    private permissionService: PermissionService
  ) {
    this.isAdminPage = this.router.url.split('/').includes('admin');
    this.leftSidebarMenuService.setIsOnAdminRoute(this.isAdminPage);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event as NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const urlSegments = event.urlAfterRedirects.split('/');
        this.isAdminPage = urlSegments.includes('admin');
        this.leftSidebarMenuService.setIsOnAdminRoute(this.isAdminPage);
      });
  }

  ngOnInit() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    /**
     * This is to set the initial value of the subTenantsDropdown form control
     * with the selected subtenants from the persistence settings
     */
    this.leftSidebarMenuService
      .getSelectedOriginatorsFromPersistence$()
      .pipe(
        switchMap((selectedOriginators) => {
          if (this.isTenantUser) {
            return this.leftSidebarMenuService.allSubTenants$.pipe(
              tap((subTenants) => {
                this.allSubTenants = subTenants;
              }),
              map((subTenants) => {
                return this.isTenantUser && selectedOriginators.length > 0
                  ? subTenants.filter((subTenant: LabelValue) =>
                      selectedOriginators.includes(subTenant.value)
                    )
                  : subTenants;
              })
            );
          } else {
            return of([
              {
                label: this.userService.subTenantId,
                value: this.userService.subTenantId,
              },
            ]);
          }
        }),
        take(1)
      )
      .subscribe((subTenants) => {
        this.subTenantForm.get('subTenantsDropdown')?.setValue(subTenants);
        this.sidebarService.setSelectedIMBs(subTenants);
        this.leftSidebarMenuService.setSelectedSubTenantIds(
          subTenants?.map((subTenant: LabelValue) => subTenant.value) || []
        );
      });

    this.subTenantForm
      .get('subTenantsDropdown')
      ?.valueChanges.pipe(
        // skip the first value because it is the initial value set in the ngOnInit
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((subTenants) => {
        this.leftSidebarMenuService.setSelectedSubTenantIds(
          subTenants?.map((subTenant: LabelValue) => subTenant.value) || []
        );
        this.sidebarService.setSelectedIMBs(subTenants || []);
      });
  }

  toggleSidebar(): void {
    this.leftSidebarMenuService.toggleSidebar();
  }

  togglePanel(panel: 'funding' | 'collateral' | 'settlement'): void {
    switch (panel) {
      case 'funding':
        this.isFundingOpen = !this.isFundingOpen;
        break;
      case 'collateral':
        this.isCollateralOpen = !this.isCollateralOpen;
        break;
      case 'settlement':
        this.isSettlementOpen = !this.isSettlementOpen;
        break;
    }
  }
}
